/**
 * S3 — Add use case: guided staged intake (DC-1781; accepted UX §7).
 *
 * Four stages plus review. The intake teaches while it collects (§7.1): each
 * stage carries a one-line statement of what the information drives. Stage 4
 * (governance) is required to be VISITED, not required to be complete (§7.5)
 * — an unanswered governance question is a recorded gap, not a blocked
 * author. On create, the user lands on the use-case workspace (§7.6) —
 * continuity beats confirmation. Nothing is suggested or computed at create
 * time; mapping is DC-1782.
 *
 * The category vocabulary renders with its template version and is the v4
 * closed list (UXT-20/§7.4); one primary + zero-or-more related (PM-UX-06).
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import {
  AUTONOMY_LEVELS,
  CATEGORIES,
  EVIDENCE_STATES,
  LIFECYCLE_STAGES,
  PREVIOUSLY_INVENTORIED,
  REVERSIBILITY_LEVELS,
  TEMPLATE_VERSION,
} from './useCaseVocabulary';
import './banking-use-cases.css';

interface DraftValues {
  useCaseRef: string;
  name: string;
  businessProblem: string;
  aiTask: string;
  intendedOutcome: string;
  accountableOwner: string;
  processLabel: string;
  nearestArcSubprocess: string;
  bundleOverrideLabel: string;
  categoryPrimary: string;
  categoriesRelated: string[];
  lifecycleStage: string;
  modelToolName: string;
  modelToolDescription: string;
  promptsRetrieval: string;
  systemsDataSources: string;
  degreeOfAutonomy: string;
  humanOversight: string;
  previouslyInventoried: string;
  governingPolicies: string;
  declaredRisks: string;
  declaredControls: string;
  materialityReach: string;
  materialityStakes: string;
  materialityReversibility: string;
  materialitySensitivity: string;
  regulationsRelevant: string;
  evidenceApplicationTape: string;
  evidenceModelScores: string;
  evidenceRealizedOutcomes: string;
  evidenceReasonCodes: string;
  evidenceMonitoringRecords: string;
  evidenceValidationReports: string;
  otherEvidenceNotes: string;
}

const EMPTY: DraftValues = {
  useCaseRef: '',
  name: '',
  businessProblem: '',
  aiTask: '',
  intendedOutcome: '',
  accountableOwner: '',
  processLabel: '',
  nearestArcSubprocess: '',
  bundleOverrideLabel: '',
  categoryPrimary: '',
  categoriesRelated: [],
  lifecycleStage: '',
  modelToolName: '',
  modelToolDescription: '',
  promptsRetrieval: '',
  systemsDataSources: '',
  degreeOfAutonomy: '',
  humanOversight: '',
  previouslyInventoried: '',
  governingPolicies: '',
  declaredRisks: '',
  declaredControls: '',
  materialityReach: '',
  materialityStakes: '',
  materialityReversibility: '',
  materialitySensitivity: '',
  regulationsRelevant: '',
  evidenceApplicationTape: '',
  evidenceModelScores: '',
  evidenceRealizedOutcomes: '',
  evidenceReasonCodes: '',
  evidenceMonitoringRecords: '',
  evidenceValidationReports: '',
  otherEvidenceNotes: '',
};

const STAGES = [
  { title: 'What it is', teach: 'Names the decision this AI touches — the anchor for every later reading.' },
  { title: 'Where it sits', teach: 'Ownership and category. The category selects which ARC controls Compass will suggest.' },
  { title: 'How it works', teach: 'The tool, its autonomy, and where a human sits. An engineer can finish this later.' },
  { title: 'How it is governed', teach: 'Policies, declared risks and controls. "Not yet determined" is a recorded answer, not a blocker.' },
] as const;

const or = (v: string): string | null => (v.trim() === '' ? null : v.trim());

export const UseCaseAdd: React.FC = () => {
  const { post } = useApiClient();
  const navigate = useNavigate();
  const [stage, setStage] = useState(0); // 0..3 stages, 4 = review
  const [values, setValues] = useState<DraftValues>(EMPTY);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [createError, setCreateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (field: keyof DraftValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setValues((v) => ({ ...v, [field]: e.target.value }));

  function stageBlockers(index: number): string[] {
    const missing: string[] = [];
    if (index === 0) {
      if (!values.name.trim()) missing.push('Use case name is required.');
      if (!values.businessProblem.trim()) missing.push('Business problem is required.');
      if (!values.aiTask.trim()) missing.push('AI task is required.');
    }
    if (index === 1) {
      if (!values.accountableOwner.trim()) missing.push('Accountable owner is required.');
      if (!values.processLabel.trim()) missing.push('Process / sub-process is required.');
      if (!values.categoryPrimary) missing.push('Primary category is required.');
      if (!values.lifecycleStage) missing.push('Lifecycle stage is required.');
    }
    if (index === 2) {
      if (!values.modelToolName.trim()) missing.push('Model / tool name is required.');
      if (!values.degreeOfAutonomy) missing.push('Degree of autonomy is required.');
    }
    // Stage 3 (governance): visited, never blocked (§7.5).
    return missing;
  }

  function next(): void {
    const missing = stageBlockers(stage);
    if (missing.length > 0) {
      setBlockers(missing);
      return;
    }
    setBlockers([]);
    setStage((s) => Math.min(s + 1, 4));
  }

  function back(): void {
    setBlockers([]);
    setStage((s) => Math.max(s - 1, 0));
  }

  async function create(): Promise<void> {
    setSubmitting(true);
    setCreateError(null);
    try {
      const body = {
        useCaseRef: or(values.useCaseRef),
        name: values.name.trim(),
        businessProblem: values.businessProblem.trim(),
        aiTask: values.aiTask.trim(),
        intendedOutcome: or(values.intendedOutcome),
        accountableOwner: values.accountableOwner.trim(),
        processLabel: values.processLabel.trim(),
        nearestArcSubprocess: or(values.nearestArcSubprocess),
        bundleOverrideLabel: or(values.bundleOverrideLabel),
        categoryPrimary: values.categoryPrimary,
        categoriesRelated: values.categoriesRelated,
        lifecycleStage: values.lifecycleStage,
        modelToolName: values.modelToolName.trim(),
        modelToolDescription: or(values.modelToolDescription),
        promptsRetrieval: or(values.promptsRetrieval),
        systemsDataSources: or(values.systemsDataSources),
        degreeOfAutonomy: values.degreeOfAutonomy,
        humanOversight: or(values.humanOversight),
        previouslyInventoried: values.previouslyInventoried || null,
        governingPolicies: or(values.governingPolicies),
        declaredRisks: or(values.declaredRisks),
        declaredControls: or(values.declaredControls),
        materialityReach: or(values.materialityReach),
        materialityStakes: or(values.materialityStakes),
        materialityReversibility: values.materialityReversibility || null,
        materialitySensitivity: or(values.materialitySensitivity),
        regulationsRelevant: or(values.regulationsRelevant),
        evidenceApplicationTape: values.evidenceApplicationTape || null,
        evidenceModelScores: values.evidenceModelScores || null,
        evidenceRealizedOutcomes: values.evidenceRealizedOutcomes || null,
        evidenceReasonCodes: values.evidenceReasonCodes || null,
        evidenceMonitoringRecords: values.evidenceMonitoringRecords || null,
        evidenceValidationReports: values.evidenceValidationReports || null,
        otherEvidenceNotes: or(values.otherEvidenceNotes),
      };
      const res = await post<{ data?: { useCase?: { id?: string } } }>(
        '/api/use-cases',
        body
      );
      const id = res.data?.useCase?.id;
      if (id) {
        navigate(`/use-cases/${id}`);
        return;
      }
      setCreateError('The use case was not created.');
    } catch (error) {
      const status = (error as { status?: number }).status;
      setCreateError(
        status === 409
          ? 'A use case with this identifier already exists in your inventory. Change the Use Case ID and try again.'
          : 'The use case was not created. Nothing was saved — your entries are intact.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  const evidenceSelect = (
    field: keyof DraftValues,
    label: string
  ): React.ReactElement => (
    <label>
      {label}
      <select value={values[field] as string} onChange={set(field)}>
        <option value="">Not answered</option>
        {EVIDENCE_STATES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="banking-page use-case-add">
      <nav className="banking-breadcrumb" aria-label="Breadcrumb">
        <Link to="/use-cases">AI Use Cases</Link> › Add use case
      </nav>
      <header className="banking-page-header">
        <div>
          <p className="banking-eyebrow">NEW AI USE CASE</p>
          <h1 className="banking-title">
            {stage < 4 ? STAGES[stage].title : 'Review & create'}
          </h1>
          <p className="banking-stage-teach">
            {stage < 4
              ? STAGES[stage].teach
              : 'Nothing runs on create. The draft lands in your inventory; ARC scope review comes next.'}
          </p>
        </div>
        <p className="banking-stage-progress" aria-label="Progress">
          Step {Math.min(stage + 1, 5)} of 5 · Template v{TEMPLATE_VERSION}
        </p>
      </header>

      {blockers.length > 0 && (
        <div className="banking-stage-blockers" data-testid="stage-blockers" role="alert">
          <ul>
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      )}

      {createError && (
        <div className="banking-create-error" data-testid="create-error" role="alert">
          {createError}
        </div>
      )}

      {stage === 0 && (
        <fieldset className="banking-stage">
          <label>
            Use Case ID (optional — generated when blank)
            <input value={values.useCaseRef} onChange={set('useCaseRef')} />
          </label>
          <label>
            Use case name
            <input value={values.name} onChange={set('name')} required />
          </label>
          <label>
            Business problem / purpose
            <textarea value={values.businessProblem} onChange={set('businessProblem')} />
          </label>
          <label>
            AI task performed
            <textarea value={values.aiTask} onChange={set('aiTask')} />
          </label>
          <label>
            Intended outcome / goal
            <textarea value={values.intendedOutcome} onChange={set('intendedOutcome')} />
          </label>
        </fieldset>
      )}

      {stage === 1 && (
        <fieldset className="banking-stage">
          <label>
            Accountable owner / unit
            <input value={values.accountableOwner} onChange={set('accountableOwner')} />
          </label>
          <label>
            Process / sub-process (your own label)
            <input value={values.processLabel} onChange={set('processLabel')} />
          </label>
          <label>
            Primary category
            <select value={values.categoryPrimary} onChange={set('categoryPrimary')}>
              <option value="">Choose a category…</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="banking-related-categories">
            <legend>Related categories (widen the future suggestion set)</legend>
            {CATEGORIES.map((c) => (
              <label key={c} className="banking-checkbox">
                <input
                  type="checkbox"
                  disabled={values.categoryPrimary === c}
                  checked={values.categoriesRelated.includes(c)}
                  onChange={(e) =>
                    setValues((v) => ({
                      ...v,
                      categoriesRelated: e.target.checked
                        ? [...v.categoriesRelated, c]
                        : v.categoriesRelated.filter((x) => x !== c),
                    }))
                  }
                />
                {c}
              </label>
            ))}
          </fieldset>
          <label>
            Lifecycle stage
            <select value={values.lifecycleStage} onChange={set('lifecycleStage')}>
              <option value="">Choose…</option>
              {LIFECYCLE_STAGES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
      )}

      {stage === 2 && (
        <fieldset className="banking-stage">
          <label>
            Model / tool / platform name
            <input value={values.modelToolName} onChange={set('modelToolName')} />
          </label>
          <label>
            Model / tool description
            <textarea
              value={values.modelToolDescription}
              onChange={set('modelToolDescription')}
            />
          </label>
          <label>
            Prompts / retrieval sources (if applicable)
            <textarea value={values.promptsRetrieval} onChange={set('promptsRetrieval')} />
          </label>
          <label>
            Systems & data sources used (semicolon-separated)
            <textarea value={values.systemsDataSources} onChange={set('systemsDataSources')} />
          </label>
          <label>
            Degree of autonomy
            <select value={values.degreeOfAutonomy} onChange={set('degreeOfAutonomy')}>
              <option value="">Choose…</option>
              {AUTONOMY_LEVELS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Human oversight point(s)
            <textarea value={values.humanOversight} onChange={set('humanOversight')} />
          </label>
        </fieldset>
      )}

      {stage === 3 && (
        <fieldset className="banking-stage">
          <label>
            Previously risk-tiered / formally inventoried?
            <select
              value={values.previouslyInventoried}
              onChange={set('previouslyInventoried')}
            >
              <option value="">Not yet determined</option>
              {PREVIOUSLY_INVENTORIED.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Governing policies (semicolon-separated)
            <textarea value={values.governingPolicies} onChange={set('governingPolicies')} />
          </label>
          <label>
            Declared key risks
            <textarea value={values.declaredRisks} onChange={set('declaredRisks')} />
          </label>
          <label>
            Declared controls in place
            <textarea value={values.declaredControls} onChange={set('declaredControls')} />
          </label>
          <label>
            Materiality — reach
            <input value={values.materialityReach} onChange={set('materialityReach')} />
          </label>
          <label>
            Materiality — decision value / stakes
            <input value={values.materialityStakes} onChange={set('materialityStakes')} />
          </label>
          <label>
            Materiality — reversibility
            <select
              value={values.materialityReversibility}
              onChange={set('materialityReversibility')}
            >
              <option value="">Not yet determined</option>
              {REVERSIBILITY_LEVELS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Materiality — sensitivity
            <input
              value={values.materialitySensitivity}
              onChange={set('materialitySensitivity')}
            />
          </label>
          <label>
            Regulations considered relevant (semicolon-separated)
            <textarea value={values.regulationsRelevant} onChange={set('regulationsRelevant')} />
          </label>
          {evidenceSelect('evidenceApplicationTape', 'Application / transaction tape available?')}
          {evidenceSelect('evidenceModelScores', 'Model scores / outputs available?')}
          {evidenceSelect('evidenceRealizedOutcomes', 'Realized outcomes available?')}
          {evidenceSelect('evidenceReasonCodes', 'Reason codes / explanations available?')}
          {evidenceSelect('evidenceMonitoringRecords', 'Monitoring records available?')}
          {evidenceSelect('evidenceValidationReports', 'Validation reports available?')}
          <label>
            Other evidence notes
            <textarea value={values.otherEvidenceNotes} onChange={set('otherEvidenceNotes')} />
          </label>
        </fieldset>
      )}

      {stage === 4 && (
        <section className="banking-review" aria-label="Review">
          <h2>Review</h2>
          <dl className="banking-review-list">
            <dt>Name</dt>
            <dd>{values.name}</dd>
            <dt>Category</dt>
            <dd>
              {values.categoryPrimary}
              {values.categoriesRelated.length > 0 &&
                ` (+ ${values.categoriesRelated.join('; ')})`}
            </dd>
            <dt>Owner</dt>
            <dd>{values.accountableOwner}</dd>
            <dt>Lifecycle</dt>
            <dd>{values.lifecycleStage}</dd>
            <dt>Autonomy</dt>
            <dd>{values.degreeOfAutonomy}</dd>
            <dt>Governance</dt>
            <dd>
              {values.governingPolicies || values.declaredRisks || values.declaredControls
                ? 'Declared'
                : 'Not yet determined — recorded as an explicit gap for the assessor.'}
            </dd>
          </dl>
          <p className="banking-review-note">
            Creating registers a draft. Nothing is suggested, computed, or run at create
            time.
          </p>
        </section>
      )}

      <div className="banking-stage-nav">
        {stage > 0 && (
          <button type="button" className="banking-btn banking-btn-secondary" onClick={back}>
            Back
          </button>
        )}
        {stage < 4 && (
          <button type="button" className="banking-btn banking-btn-primary" onClick={next}>
            Next
          </button>
        )}
        {stage === 4 && (
          <button
            type="button"
            className="banking-btn banking-btn-primary"
            disabled={submitting}
            onClick={() => void create()}
          >
            Create use case
          </button>
        )}
      </div>
    </div>
  );
};

export default UseCaseAdd;
